import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { format } from 'date-fns';
import { 
  BuildingOfficeIcon,
  SparklesIcon,
  MapPinIcon,
  WrenchScrewdriverIcon,
  ExclamationCircleIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import useAuthStore from '../store/authStore';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import SimplePullToRefresh from '../components/SimplePullToRefresh';
import { statusLabels, getStatusStyle } from '../config/statusColors';
import HighlightedText, { HighlightedTextWithContext } from '../components/HighlightedText';

const ProjectList = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    page: 1,
    limit: 20
  });
  const [showFilters, setShowFilters] = useState(false);
  const [viewLogsModal, setViewLogsModal] = useState({
    show: false,
    projectId: null,
    userId: null,
    userName: '',
    userType: ''
  });

  // URL 파라미터에서 검색어와 필터 가져오기
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    const urlFilter = searchParams.get('filter');
    const urlStatus = searchParams.get('status');
    const urlPage = searchParams.get('page');
    
    if (urlSearch) {
      setSearchInput(urlSearch);
      setSearchQuery(urlSearch);
    }
    
    // 페이지 파라미터가 있으면 페이지 설정
    if (urlPage) {
      setFilters(prev => ({ ...prev, page: parseInt(urlPage, 10) }));
    }
    
    // filter=received 또는 status=received 파라미터가 있으면 필터 자동 적용
    if (urlFilter === 'received' || urlStatus === 'received') {
      setFilters(prev => ({ ...prev, status: 'received' }));
      setShowFilters(true); // 필터 패널 열기
    } else if (urlStatus) {
      // 다른 상태값도 지원
      setFilters(prev => ({ ...prev, status: urlStatus }));
      setShowFilters(true);
    }
  }, [searchParams]);

  // 필터 상태 변경 시 스크롤 위치를 최상단으로 리셋
  useEffect(() => {
    // 필터 상태가 변경되면 스크롤을 최상단으로 이동
    window.scrollTo(0, 0);
    
    // SimplePullToRefresh 내부의 스크롤 컨테이너도 리셋
    const scrollContainer = document.querySelector('.pull-to-refresh-content');
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    }
  }, [filters.status, filters.page]);

  // 검색 디바운스 처리 (응답성 향상을 위해 200ms로 단축)
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      setFilters(prev => ({ ...prev, page: 1 })); // 검색 시 첫 페이지로
    }, 200);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // 프로젝트 목록 조회
  const { data, isLoading, error, refetch } = useQuery(
    ['projects', filters, searchQuery],
    () => {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (searchQuery) params.append('search', searchQuery);
      params.append('page', filters.page);
      params.append('limit', filters.limit);
      
      return api.get(`/projects?${params.toString()}`).then(res => res.data);
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      staleTime: 5000 // 5초
    }
  );

  // 조회 이력 조회
  const { data: viewLogs } = useQuery(
    ['viewLogs', viewLogsModal.projectId, viewLogsModal.userId],
    () => api.get(`/projects/${viewLogsModal.projectId}/view-logs/${viewLogsModal.userId}`).then(res => res.data),
    {
      enabled: viewLogsModal.show && !!viewLogsModal.projectId && !!viewLogsModal.userId,
      staleTime: 0
    }
  );

  // Pull to Refresh 핸들러
  const handleRefresh = async () => {
    const prevProjectIds = projects.map(p => p.id);
    const result = await refetch();
    
    // 새로운 프로젝트 개수 계산
    if (result.data) {
      const newProjects = result.data.projects || [];
      const newProjectIds = newProjects.map(p => p.id);
      const newCount = newProjectIds.filter(id => !prevProjectIds.includes(id)).length;
      return newCount;
    }
    return 0;
  };

  const getStatusBadge = (status) => {
    // 상태값이 숫자로 오는 경우 처리
    const statusMap = {
      '0': 'received',
      '1': 'review',
      '2': 'contract',
      '3': 'completed',
      '4': 'cancelled'
    };
    
    const mappedStatus = statusMap[String(status)] || status;
    const text = statusLabels[mappedStatus] || '알 수 없음';
    const color = getStatusStyle(mappedStatus, 'light') || 'bg-gray-50 text-gray-700 border border-gray-300';
    
    return { text, color };
  };

  const getConstructionTypeIcon = (types) => {
    if (!types || types.length === 0) return <WrenchScrewdriverIcon className="h-5 w-5 text-gray-400" />;
    
    const firstType = types.split(',')[0];
    if (firstType.includes('방수')) return <SparklesIcon className="h-5 w-5 text-blue-500" />;
    if (firstType.includes('단열')) return <BuildingOfficeIcon className="h-5 w-5 text-orange-500" />;
    if (firstType.includes('철거')) return <ExclamationCircleIcon className="h-5 w-5 text-red-500" />;
    return <WrenchScrewdriverIcon className="h-5 w-5 text-gray-500" />;
  };

  const formatEstimatedAmount = (amount) => {
    if (!amount) return '-';
    const billion = Math.floor(amount / 100000000);
    const million = Math.floor((amount % 100000000) / 10000);
    
    if (billion > 0) {
      return `${billion}억${million > 0 ? ` ${million}만` : ''}원`;
    }
    return `${million}만원`;
  };

  const calculateDaysAgo = (date) => {
    const today = new Date();
    const createdDate = new Date(date);
    const diffTime = Math.abs(today - createdDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return '오늘';
    if (diffDays === 1) return '어제';
    if (diffDays < 7) return `${diffDays}일 전`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}주 전`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}개월 전`;
    return `${Math.floor(diffDays / 365)}년 전`;
  };

  // 제목 컴포넌트 - 잘림 감지 및 페이드 효과
  const ProjectTitle = ({ title, searchTerm }) => {
    const titleRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      const checkTruncation = () => {
        if (titleRef.current) {
          const element = titleRef.current;
          setIsTruncated(element.scrollWidth > element.clientWidth);
        }
      };

      checkTruncation();
      window.addEventListener('resize', checkTruncation);
      return () => window.removeEventListener('resize', checkTruncation);
    }, [title]);

    return (
      <div className="relative flex-1 min-w-0">
        <h4 
          ref={titleRef}
          className="font-extrabold text-gray-900 text-base overflow-hidden whitespace-nowrap" 
          title={title}
        >
          <HighlightedText 
            text={title} 
            searchTerm={searchTerm}
            isTitle={true}
          />
        </h4>
        {isTruncated && (
          <div 
            className="absolute right-0 top-0 bottom-0 w-12 pointer-events-none"
            style={{
              background: 'linear-gradient(to right, rgba(239, 246, 255, 0) 0%, rgba(239, 246, 255, 0.5) 30%, rgba(239, 246, 255, 0.9) 70%, rgba(239, 246, 255, 1) 100%)'
            }}
          />
        )}
      </div>
    );
  };

  const EmptyState = () => (
    <div className="text-center py-16">
      {searchQuery ? (
        <>
          <ExclamationCircleIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">"{searchQuery}"에 대한 검색 결과가 없습니다.</p>
          <button 
            onClick={() => {
              setSearchInput('');
              setSearchQuery('');
            }}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            검색 초기화
          </button>
        </>
      ) : (
        <>
          <ExclamationCircleIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">
            {filters.status ? '해당 상태의 프로젝트가 없습니다.' : '프로젝트가 없습니다.'}
          </p>
        </>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className={`min-h-screen ${user?.user_type === 'vip_customer' ? 'bg-gradient-to-br from-purple-50 via-white to-pink-50' : 'bg-gray-50'}`}>
        {/* VIP 고객 전용 헤더 */}
        {user?.user_type === 'vip_customer' && (
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <StarIcon className="h-5 w-5 text-yellow-300" />
                <span className="text-sm font-medium">VIP 고객님 환영합니다</span>
              </div>
              <button
                onClick={() => navigate('/vip')}
                className="text-sm font-medium hover:text-yellow-300 transition-colors"
              >
                내 리워드 확인 →
              </button>
            </div>
          </div>
        )}

        {/* 공통 헤더 */}
        <PageHeader 
          title="프로젝트 관리"
          totalCount={0}
          filters={filters}
          setFilters={setFilters}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          setSearchQuery={setSearchQuery}
          showCreateButton={true}
          isHome={false}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
        />

        <div className="max-w-7xl mx-auto p-4">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-500">데이터를 불러오는 중...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen ${user?.user_type === 'vip_customer' ? 'bg-gradient-to-br from-purple-50 via-white to-pink-50' : 'bg-gray-50'}`}>
        {/* VIP 고객 전용 헤더 */}
        {user?.user_type === 'vip_customer' && (
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <StarIcon className="h-5 w-5 text-yellow-300" />
                <span className="text-sm font-medium">VIP 고객님 환영합니다</span>
              </div>
              <button
                onClick={() => navigate('/vip')}
                className="text-sm font-medium hover:text-yellow-300 transition-colors"
              >
                내 리워드 확인 →
              </button>
            </div>
          </div>
        )}

        {/* 공통 헤더 */}
        <PageHeader 
          title="프로젝트 관리"
          totalCount={0}
          filters={filters}
          setFilters={setFilters}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          setSearchQuery={setSearchQuery}
          showCreateButton={true}
          isHome={false}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
        />

        <div className="max-w-7xl mx-auto p-4">
          <div className="text-center py-16">
            <ExclamationCircleIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4 font-medium">데이터를 불러올 수 없습니다</p>
            <button 
              onClick={() => window.location.reload()} 
              className="btn-primary"
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }

  const projects = data?.projects || [];

  return (
    <SimplePullToRefresh onRefresh={handleRefresh} refreshMessage="프로젝트 목록이 업데이트되었습니다!" showDataCount={true}>
      <div className={`min-h-screen ${user?.user_type === 'vip_customer' ? 'bg-gradient-to-br from-purple-50 via-white to-pink-50' : 'bg-gray-50'}`}>
      {/* VIP 고객 전용 헤더 */}
      {user?.user_type === 'vip_customer' && (
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <StarIcon className="h-5 w-5 text-yellow-300" />
              <span className="text-sm font-medium">VIP 고객님 환영합니다</span>
            </div>
            <button
              onClick={() => navigate('/vip')}
              className="text-sm font-medium hover:text-yellow-300 transition-colors"
            >
              내 리워드 확인 →
            </button>
          </div>
        </div>
      )}

      {/* 공통 헤더 */}
      <PageHeader 
        title="프로젝트 관리"
        totalCount={data?.total || 0}
        filters={filters}
        setFilters={setFilters}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        setSearchQuery={setSearchQuery}
        showCreateButton={true}
        isHome={false}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
      />

      {/* 컨텐츠 */}
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-6">
        {projects.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-4">
            {projects.map((project) => {
              console.log('Link state 전달:', { 
                from: '/projects',
                page: filters.page,
                status: filters.status,
                search: searchQuery 
              });
              return (
                    <Link
                      key={project.id}
                      to={`/projects/${project.id}`}
                      state={{ 
                        from: '/projects',
                        page: filters.page,
                        status: filters.status,
                        search: searchQuery 
                      }}
                      className="block bg-white rounded-xl shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-200 overflow-hidden group relative"
                    >
                      
                      {/* 상단 헤더 */}
                      <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-3 border-b border-blue-200">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="inline-flex items-center justify-center bg-blue-600 text-white text-xs font-bold px-2.5 py-1 rounded-full flex-shrink-0">{project.id}</span>
                            <ProjectTitle title={project.title} searchTerm={searchQuery} />
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {!!project.is_urgent && (
                              <span className="inline-flex items-center px-3 py-1.5 text-xs font-bold rounded-md bg-red-50 text-red-700 border border-red-300 relative overflow-hidden">
                                <span className="inline-block animate-bounce" style={{ animationDuration: '1s' }}>🚨</span>
                                <span className="ml-1">긴급</span>
                              </span>
                            )}
                            <span className={`inline-flex items-center px-3 py-1.5 text-xs font-bold rounded-md ${getStatusBadge(project.status).color}`}>
                              {getStatusBadge(project.status).text}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="p-3.5 space-y-3.5">
                        {/* 주소와 공사유형 */}
                        {(project.address || project.construction_types) && (
                          <div className="space-y-2">
                            {project.address && (
                              <div className="flex items-center gap-2">
                                <div className="bg-blue-50 p-1.5 rounded flex-shrink-0">
                                  <MapPinIcon className="h-3 w-3 text-blue-600" style={{ strokeWidth: 1.5 }} />
                                </div>
                                <span className="text-sm text-gray-600">
                                  <HighlightedText 
                                    text={project.address} 
                                    searchTerm={searchQuery}
                                  />
                                </span>
                              </div>
                            )}
                            {project.construction_types && user?.user_type !== 'contractor' && (
                              <div className="flex items-center gap-2">
                                <div className="bg-purple-50 p-1.5 rounded flex-shrink-0">
                                  <WrenchScrewdriverIcon className="h-3 w-3 text-purple-600" style={{ strokeWidth: 1.5 }} />
                                </div>
                                <span className="text-sm text-gray-600">
                                  <HighlightedText 
                                    text={project.construction_types} 
                                    searchTerm={searchQuery}
                                  />
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* 주요 정보 테이블 */}
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-3.5 border border-blue-100">
                          <div className="space-y-2.5">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-500 font-medium">고객명</span>
                              <span className="text-sm text-gray-900 font-bold inline-flex items-center gap-1">
                                {project.customer_user_type === 'vip_customer' && (
                                  <span className="px-1.5 text-xs font-bold bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded relative" style={{ top: '-2px' }}>
                                    VIP
                                  </span>
                                )}
                                {user?.user_type === 'admin' && (
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setViewLogsModal({
                                        show: true,
                                        projectId: project.id,
                                        userId: project.customer_id,
                                        userName: project.customer_name,
                                        userType: 'customer'
                                      });
                                    }}
                                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                                      project.customer_view_count > 0 
                                        ? (project.customer_view_count >= 4 ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-blue-100 text-blue-700 hover:bg-blue-200')
                                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                    }`}
                                    title="클릭하여 조회 이력 보기"
                                  >
                                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                                      project.customer_view_count > 0 
                                        ? (project.customer_view_count >= 4 ? 'bg-green-500' : 'bg-blue-500')
                                        : 'bg-gray-400'
                                    }`}></span>
                                    <span>{project.customer_view_count || 0}</span>
                                  </button>
                                )}
                                <span>
                                  <HighlightedText 
                                    text={project.customer_name} 
                                    searchTerm={searchQuery}
                                  />
                                  <span> 고객님</span>
                                </span>
                              </span>
                            </div>
                            {user?.user_type !== 'contractor' && (
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500 font-medium">담당자</span>
                                <span className="text-sm text-gray-900 font-bold inline-flex items-center gap-1">
                                  {user?.user_type === 'admin' && project.contractor_name && (
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setViewLogsModal({
                                          show: true,
                                          projectId: project.id,
                                          userId: project.contractor_id,
                                          userName: project.contractor_name,
                                          userType: 'contractor'
                                        });
                                      }}
                                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                                        project.contractor_view_count > 0 
                                          ? (project.contractor_view_count >= 4 ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-blue-100 text-blue-700 hover:bg-blue-200')
                                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                      }`}
                                      title="클릭하여 조회 이력 보기"
                                    >
                                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                                        project.contractor_view_count > 0 
                                          ? (project.contractor_view_count >= 4 ? 'bg-green-500' : 'bg-blue-500')
                                          : 'bg-gray-400'
                                      }`}></span>
                                      <span>{project.contractor_view_count || 0}</span>
                                    </button>
                                  )}
                                  {project.contractor_name ? (
                                    <span>{project.contractor_name} 소장님</span>
                                  ) : (
                                    <span className="text-orange-600">배정 대기중</span>
                                  )}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-500 font-medium">
                                {project.status === 'completed' ? '완료일' : 
                                 project.status === 'cancelled' ? '취소일' : '접수일'}
                              </span>
                              <span className="text-sm text-gray-900 font-bold">
                                {format(new Date(
                                  project.status === 'completed' || project.status === 'cancelled' 
                                    ? project.updated_at 
                                    : project.created_at
                                ), 'yyyy.MM.dd')}
                              </span>
                            </div>
                            {(project.status === 'completed' || project.status === 'cancelled') && 
                             (project.contract_amount || project.estimated_amount) && (
                              <>
                                <div className="border-t border-blue-200 pt-3"></div>
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-gray-500 font-medium">계약금액</span>
                                  <span className="text-base text-gray-900 font-bold">
                                    {formatEstimatedAmount(project.contract_amount || project.estimated_amount)}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* 프로젝트 설명 */}
                        {project.description && (
                          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-3 border border-indigo-100">
                            <p className="text-sm text-indigo-900 line-clamp-2">
                              <HighlightedTextWithContext 
                                text={project.description} 
                                searchTerm={searchQuery}
                                maxLength={90}
                              />
                            </p>
                          </div>
                        )}

                        {/* 취소 사유 */}
                        {project.status === 'cancelled' && project.cancel_reason && (
                          <div className="bg-red-50 rounded-xl p-3 border border-red-100">
                            <p className="text-sm text-red-800 leading-relaxed">
                              <span className="font-bold">취소 사유:</span> {project.cancel_reason}
                            </p>
                          </div>
                        )}

                        {/* 하단 액션 영역 */}
                        <div className="flex items-center justify-between pt-1">
                          <div className="flex items-center gap-3">
                            {project.customer_email && (
                              <span className="text-xs text-gray-500 flex items-center gap-1.5">
                                <div className="bg-blue-100 p-1 rounded">
                                  <svg className="h-3 w-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                </div>
                                <span className="text-gray-600">{project.customer_email}</span>
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {project.status === 'completed' && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full font-medium">
                                {(() => {
                                  const endDate = project.completed_at;
                                  const startDate = project.contract_date;
                                  
                                  if (!endDate || !startDate) {
                                    return '기간 계산 불가';
                                  }
                                  
                                  const days = Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24));
                                  const daysText = days < 0 ? '0일' : `${days}일`;
                                  
                                  return `계약→완료: ${daysText}`;
                                })()}
                              </span>
                            )}
                            {project.unread_messages > 0 && (
                              <span className="relative inline-flex items-center text-xs bg-red-500 text-white px-3 py-1.5 rounded-full font-bold shadow-sm">
                                <span className="absolute inset-0 bg-red-400 rounded-full animate-ping opacity-75"></span>
                                <ChatBubbleLeftRightIcon className="relative h-3.5 w-3.5 mr-1.5" />
                                <span className="relative">새 메시지 {project.unread_messages}</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* 진행률 바 */}
                      {project.status === 'contract' && project.progress !== undefined && (
                        <div className="relative h-2 bg-blue-100">
                          <div 
                            className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
                            style={{ width: `${project.progress || 0}%` }}
                          >
                            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full shadow-sm"></div>
                          </div>
                        </div>
                      )}
                    </Link>
              );
            })}
          </div>
        )}

        {/* 페이지네이션 */}
        {data?.pages > 1 && (
          <div className="mt-8 flex justify-center">
            <nav className="flex items-center gap-2">
              <button
                onClick={() => setFilters(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={filters.page === 1}
                className="px-3 py-1 text-sm rounded-md bg-white border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                이전
              </button>
              
              {[...Array(Math.min(5, data.pages))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setFilters(prev => ({ ...prev, page: pageNum }))}
                    className={`px-3 py-1 text-sm rounded-md ${
                      filters.page === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => setFilters(prev => ({ ...prev, page: Math.min(data.pages, prev.page + 1) }))}
                disabled={filters.page === data.pages}
                className="px-3 py-1 text-sm rounded-md bg-white border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                다음
              </button>
            </nav>
          </div>
        )}
        </div>
      </div>
    </div>
    
    {/* 조회 이력 모달 */}
    {viewLogsModal.show && (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold">
              {viewLogsModal.userName} {viewLogsModal.userType === 'customer' ? '고객님' : '소장님'} 조회 이력
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              총 {viewLogsModal.userType === 'customer' 
                ? projects.find(p => p.id === viewLogsModal.projectId)?.customer_view_count 
                : projects.find(p => p.id === viewLogsModal.projectId)?.contractor_view_count}회 조회
            </p>
          </div>
          
          <div className="p-4 overflow-y-auto max-h-[60vh]">
            {viewLogs && viewLogs.length > 0 ? (
              <div className="space-y-3">
                {viewLogs.map((log, index) => (
                  <div key={log.id} className="flex items-start gap-3 pb-3 border-b last:border-0">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium text-gray-600">
                        {index + 1}
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {log.formatted_time}
                      </p>
                      <p className="text-xs text-gray-500">
                        {log.relative_time}
                      </p>
                      {log.user_agent && (
                        <p className="text-xs text-gray-400 mt-1 truncate">
                          {log.user_agent.includes('Mobile') ? '📱 모바일' : '💻 데스크톱'}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">
                조회 이력이 없습니다.
              </p>
            )}
          </div>
          
          <div className="p-4 border-t">
            <button
              onClick={() => setViewLogsModal({
                show: false,
                projectId: null,
                userId: null,
                userName: '',
                userType: ''
              })}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    )}
    </SimplePullToRefresh>
  );
};

export default ProjectList;